'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HexCalculator() {
  const t = useTranslations("tools.hexCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator states
  const [hexNum1, setHexNum1] = useState('');
  const [hexNum2, setHexNum2] = useState('');
  const [operator, setOperator] = useState('+');
  const [calcResult, setCalcResult] = useState<{ hex: string; decimal: number } | null>(null);
  const [calcError, setCalcError] = useState('');

  // Hex to Decimal converter states
  const [hexInput, setHexInput] = useState('');
  const [h2dResult, setH2dResult] = useState<{ decimal: number; binary: string; octal: string } | null>(null);
  const [h2dError, setH2dError] = useState('');

  // Decimal to Hex converter states
  const [decInput, setDecInput] = useState('');
  const [d2hResult, setD2hResult] = useState<{ hex: string; binary: string; octal: string } | null>(null);
  const [d2hError, setD2hError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Utility functions
  const isValidHex = (str: string): boolean => {
    return /^[0-9A-Fa-f]+$/.test(str);
  };

  const hexToDecimal = (hex: string): number => {
    return parseInt(hex, 16);
  };

  const decimalToHex = (dec: number): string => {
    return dec.toString(16).toUpperCase();
  };

  const decimalToBinary = (dec: number): string => {
    return dec.toString(2);
  };

  const decimalToOctal = (dec: number): string => {
    return dec.toString(8);
  };

  // Calculator handlers
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setCalcError('');
    setCalcResult(null);

    const num1 = hexNum1.trim().toUpperCase();
    const num2 = hexNum2.trim().toUpperCase();

    if (!num1 || !num2) {
      setCalcError(t('calcErrorEmpty'));
      return;
    }

    if (!isValidHex(num1) || !isValidHex(num2)) {
      setCalcError(t('calcErrorInvalid'));
      return;
    }

    const dec1 = hexToDecimal(num1);
    const dec2 = hexToDecimal(num2);
    let result: number;

    switch(operator) {
      case '+':
        result = dec1 + dec2;
        break;
      case '-':
        result = dec1 - dec2;
        break;
      case '*':
        result = dec1 * dec2;
        break;
      case '/':
        if (dec2 === 0) {
          setCalcError(t('calcErrorDivideZero'));
          return;
        }
        result = Math.floor(dec1 / dec2);
        break;
      default:
        return;
    }

    const hexResult = result < 0 ? '-' + decimalToHex(Math.abs(result)) : decimalToHex(result);
    setCalcResult({ hex: hexResult, decimal: result });
  };

  const handleClearCalc = () => {
    setHexNum1('');
    setHexNum2('');
    setCalcResult(null);
    setCalcError('');
  };

  // Hex to Decimal handlers
  const handleHexToDec = (e: React.FormEvent) => {
    e.preventDefault();
    setH2dError('');
    setH2dResult(null);

    const hex = hexInput.trim().toUpperCase();

    if (!hex) {
      setH2dError(t('h2dErrorEmpty'));
      return;
    }

    if (!isValidHex(hex)) {
      setH2dError(t('h2dErrorInvalid'));
      return;
    }

    const decimal = hexToDecimal(hex);
    const binary = decimalToBinary(decimal);
    const octal = decimalToOctal(decimal);

    setH2dResult({ decimal, binary, octal });
  };

  const handleClearH2d = () => {
    setHexInput('');
    setH2dResult(null);
    setH2dError('');
  };

  // Decimal to Hex handlers
  const handleDecToHex = (e: React.FormEvent) => {
    e.preventDefault();
    setD2hError('');
    setD2hResult(null);

    const dec = decInput.trim();

    if (!dec) {
      setD2hError(t('d2hErrorEmpty'));
      return;
    }

    const decimal = parseInt(dec, 10);

    if (isNaN(decimal) || decimal < 0) {
      setD2hError(t('d2hErrorInvalid'));
      return;
    }

    const hex = decimalToHex(decimal);
    const binary = decimalToBinary(decimal);
    const octal = decimalToOctal(decimal);

    setD2hResult({ hex, binary, octal });
  };

  const handleClearD2h = () => {
    setDecInput('');
    setD2hResult(null);
    setD2hError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    purple: '#7c3aed',
    green: '#15803d',
    greenLight: '#dcfce7',
    greenBg: '#f0fdf4',
    error: '#ef4444',
    gray: '#64748b',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { color: colors.blue, marginBottom: '1.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const calculatorSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem' },
    { flexDirection: 'column' as const, alignItems: 'stretch' as const },
    { flexDirection: 'row' as const, flexWrap: 'wrap' as const },
    { flexDirection: 'row' as const, flexWrap: 'nowrap' as const }
  );

  const hexInputStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: `2px solid #e2e8f0`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontFamily: "'Courier New', monospace",
      textTransform: 'uppercase' as const,
      transition: 'all 0.3s ease',
    },
    { width: '100%' },
    { width: '200px' },
    { width: '200px' }
  );

  const operatorSelectStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1.25rem',
      fontWeight: 600,
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textAlign: 'center' as const,
    },
    { width: '100%' },
    { width: '80px' },
    { width: '80px' }
  );

  const equalsSignStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.gray,
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const, flexWrap: 'wrap' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      minWidth: '120px',
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: colors.textSecondary,
      minWidth: '120px',
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const resultsStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginTop: '1.5rem',
    border: '2px solid #86efac',
  };

  const resultsHeaderStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    color: colors.green,
    marginBottom: '1rem',
    fontWeight: 600,
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
  };

  const resultLabelStyle: React.CSSProperties = {
    fontWeight: 500,
    color: colors.textSecondary,
  };

  const resultValueStyle: React.CSSProperties = {
    fontFamily: "'Courier New', monospace",
    fontSize: '1.125rem',
    fontWeight: 700,
    color: colors.blue,
  };

  const errorMessageStyle: React.CSSProperties = {
    color: colors.error,
    fontSize: '0.875rem',
    marginTop: '0.5rem',
    marginBottom: '1rem',
  };

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.textSecondary,
  };

  const conversionInputStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease',
      width: '100%',
    },
    { maxWidth: '100%' },
    { maxWidth: '400px' },
    { maxWidth: '400px' }
  );

  const conversionTableStyle: React.CSSProperties = {
    marginTop: '1.5rem',
    background: 'white',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  };

  const conversionTableHeaderStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: colors.textSecondary,
    marginBottom: '1rem',
    fontWeight: 600,
  };

  const conversionGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const conversionCardStyle: React.CSSProperties = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
  };

  const conversionCardTitleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: colors.gray,
    marginBottom: '0.5rem',
  };

  const conversionCardValueStyle: React.CSSProperties = {
    fontFamily: "'Courier New', monospace",
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.textPrimary,
    wordBreak: 'break-all' as const,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle: React.CSSProperties = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.blue,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          {/* Hexadecimal Calculation */}
          <div style={calculatorSectionStyle}>
            <h3 style={sectionTitleStyle}>⚡ {t('calcTitle')}</h3>
            <form onSubmit={handleCalculate}>
              <div style={formRowStyle}>
                <input
                  type="text"
                  style={hexInputStyle}
                  value={hexNum1}
                  onChange={(e) => setHexNum1(e.target.value.toUpperCase())}
                  placeholder={t('calcPlaceholder1')}
                  maxLength={16}
                />
                <select
                  style={operatorSelectStyle}
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                >
                  <option value="+">+</option>
                  <option value="-">−</option>
                  <option value="*">×</option>
                  <option value="/">÷</option>
                </select>
                <input
                  type="text"
                  style={hexInputStyle}
                  value={hexNum2}
                  onChange={(e) => setHexNum2(e.target.value.toUpperCase())}
                  placeholder={t('calcPlaceholder2')}
                  maxLength={16}
                />
                <span style={equalsSignStyle}>=</span>
              </div>
              {calcError && <div style={errorMessageStyle}>{calcError}</div>}
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={handleClearCalc}>{t('clearButton')}</button>
              </div>
            </form>
            {calcResult && (
              <div style={resultsStyle}>
                <h3 style={resultsHeaderStyle}>📊 {t('resultTitle')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultHex')}:</span>
                  <span style={resultValueStyle}>{calcResult.hex}</span>
                </div>
                <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                  <span style={resultLabelStyle}>{t('resultDecimal')}:</span>
                  <span style={resultValueStyle}>{calcResult.decimal.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Hex to Decimal Converter */}
          <div style={calculatorSectionStyle}>
            <h3 style={sectionTitleStyle}>🔄 {t('h2dTitle')}</h3>
            <form onSubmit={handleHexToDec}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="hex-input">{t('h2dLabel')}:</label>
                <input
                  type="text"
                  style={conversionInputStyle}
                  id="hex-input"
                  value={hexInput}
                  onChange={(e) => setHexInput(e.target.value.toUpperCase())}
                  placeholder={t('h2dPlaceholder')}
                  maxLength={16}
                />
              </div>
              {h2dError && <div style={errorMessageStyle}>{h2dError}</div>}
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={handleClearH2d}>{t('clearButton')}</button>
              </div>
            </form>
            {h2dResult && (
              <div style={resultsStyle}>
                <h3 style={resultsHeaderStyle}>📊 {t('conversionResult')}</h3>
                <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                  <span style={resultLabelStyle}>{t('decimalValue')}:</span>
                  <span style={resultValueStyle}>{h2dResult.decimal.toLocaleString()}</span>
                </div>
                <div style={conversionTableStyle}>
                  <h4 style={conversionTableHeaderStyle}>{t('additionalConversions')}:</h4>
                  <div style={conversionGridStyle}>
                    <div style={conversionCardStyle}>
                      <div style={conversionCardTitleStyle}>{t('binary')}</div>
                      <div style={conversionCardValueStyle}>{h2dResult.binary}</div>
                    </div>
                    <div style={conversionCardStyle}>
                      <div style={conversionCardTitleStyle}>{t('octal')}</div>
                      <div style={conversionCardValueStyle}>{h2dResult.octal}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Decimal to Hex Converter */}
          <div style={calculatorSectionStyle}>
            <h3 style={sectionTitleStyle}>🔄 {t('d2hTitle')}</h3>
            <form onSubmit={handleDecToHex}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="dec-input">{t('d2hLabel')}:</label>
                <input
                  type="text"
                  style={conversionInputStyle}
                  id="dec-input"
                  value={decInput}
                  onChange={(e) => setDecInput(e.target.value)}
                  placeholder={t('d2hPlaceholder')}
                  maxLength={16}
                />
              </div>
              {d2hError && <div style={errorMessageStyle}>{d2hError}</div>}
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={handleClearD2h}>{t('clearButton')}</button>
              </div>
            </form>
            {d2hResult && (
              <div style={resultsStyle}>
                <h3 style={resultsHeaderStyle}>📊 {t('conversionResult')}</h3>
                <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                  <span style={resultLabelStyle}>{t('hexValue')}:</span>
                  <span style={resultValueStyle}>{d2hResult.hex}</span>
                </div>
                <div style={conversionTableStyle}>
                  <h4 style={conversionTableHeaderStyle}>{t('additionalConversions')}:</h4>
                  <div style={conversionGridStyle}>
                    <div style={conversionCardStyle}>
                      <div style={conversionCardTitleStyle}>{t('binary')}</div>
                      <div style={conversionCardValueStyle}>{d2hResult.binary}</div>
                    </div>
                    <div style={conversionCardStyle}>
                      <div style={conversionCardTitleStyle}>{t('octal')}</div>
                      <div style={conversionCardValueStyle}>{d2hResult.octal}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsPara1')}</p>
        <p style={paragraphStyle}>{t('insightsPara2')}</p>
        <p style={paragraphStyle}>{t('insightsPara3')}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
          <li style={listItemStyle}>{t('practice6')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://deepseekaiapi.com/hex-calculator" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.io/hex-calculator/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://calculatorit.com/hex-calculator/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.upseotool.com/hex-calculator" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://hexcalculator.org/shift/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://hex.tech/blog/use-cases-for-calculations-in-hex-now-with-input-parameters/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}